import googleMapsService from './googleMapsService';
import aiService from './aiService';
import { findMichelinGuide } from './michelinGuideService';
import { getScheduleInfo } from '../utils/schedulePlanner';
import ratingPrior from '../data/rankingPrior.json';

const TYPE_LABELS = {
  restaurant: '餐廳', cafe: '咖啡', bakery: '甜點', meal_takeaway: '外帶',
  japanese_restaurant: '日式', chinese_restaurant: '中式', italian_restaurant: '義式',
  vegetarian_restaurant: '素食', vegan_restaurant: '純素'
};

class RecommendationService {
  getRecommendations(places, userLocation, preferences = {}) {
    const maxDistance = Number.isFinite(preferences.distance) && preferences.distance > 0 ? preferences.distance : 1200;
    const now = Date.now();
    const unique = [...new Map((places || []).filter(place => place?.place_id).map(place => [place.place_id, place])).values()];
    const filtered = unique.map(place => ({
      ...place,
      _distance: googleMapsService.calculateDistance(userLocation, place.geometry?.location)
    })).filter(place => {
      if (preferences.excludeIds?.includes(place.place_id)) return false;
      if (place.business_status && place.business_status !== 'OPERATIONAL') return false;
      if (!Number.isFinite(place._distance) || place._distance > maxDistance) return false;
      if (preferences.openNow && !place.opening_hours?.open_now) return false;
      if (preferences.priceRange && preferences.priceRange !== 'all') {
        if (!Number.isInteger(place.price_level) || place.price_level < 0 || place.price_level > 4) return false;
        if (preferences.priceRange === 'low' && place.price_level > 1) return false;
        if (preferences.priceRange === 'medium' && place.price_level !== 2) return false;
        if (preferences.priceRange === 'high' && place.price_level < 3) return false;
      }
      return true;
    });

    // Favorites indicate interest, not proof the user enjoyed a meal. Use only known categories and prices.
    const interestTypes = place => (Array.isArray(place.types) ? place.types : []).filter(type =>
      typeof type === 'string' && ((type.endsWith('_restaurant') && type !== 'restaurant') || ['cafe', 'bakery', 'meal_takeaway'].includes(type)));
    const favorites = [...new Map((preferences.favorites || []).filter(place => place?.place_id).map(place => [place.place_id, place])).values()];
    const typedFavorites = favorites.filter(place => interestTypes(place).length);
    const pricedFavorites = favorites.filter(place => Number.isInteger(place.price_level) && place.price_level >= 0 && place.price_level <= 4);
    const personalized = typedFavorites.length >= 2 || pricedFavorites.length >= 2;

    const ranked = filtered.map(place => {
      const distance = place._distance;
      const rating = Number.isFinite(place.rating) && place.rating >= 1 && place.rating <= 5 ? place.rating : null;
      const reviewCount = Math.max(0, Number.isFinite(place.user_ratings_total) ? place.user_ratings_total : 0);
      const scheduleInfo = getScheduleInfo(place, userLocation, preferences.schedule, now);
      // Bayesian prior from src/data/rankingPrior.json; scripts/calibrate-ranking.cjs refits it from visit feedback.
      const reliableRating = rating == null ? ratingPrior.mean : (rating * reviewCount + ratingPrior.mean * ratingPrior.weight) / (reviewCount + ratingPrior.weight);
      const visit = preferences.visitOutcomes?.[place.place_id];
      const visitReason = visit === 'liked' ? '你上次說好吃' : visit === 'meh' ? '你上次覺得普通' : null;
      const typeMatch = typedFavorites.length < 2 ? 0 : typedFavorites.filter(saved => interestTypes(saved).some(type => interestTypes(place).includes(type))).length / typedFavorites.length;
      const priceMatch = pricedFavorites.length < 2 || !Number.isInteger(place.price_level) ? 0 : pricedFavorites.filter(saved => saved.price_level === place.price_level).length / pricedFavorites.length;
      const interestReason = typeMatch >= 0.5 ? '類型接近你的收藏' : priceMatch >= 0.5 ? '價位接近你的收藏' : null;
      const score = reliableRating * (preferences.ignoreRating ? 0 : personalized ? 6 : 10) + typeMatch * 12 + priceMatch * 4 +
        Math.max(0, 28 * (1 - distance / maxDistance)) +
        (place.opening_hours?.open_now ? 6 : 0) +
        (scheduleInfo?.status === 'safe' ? 8 : 0) +
        (visit === 'liked' ? 10 : visit === 'meh' ? -12 : 0);
      return {
        ...place,
        rating, user_ratings_total: Math.floor(reviewCount),
        price_level: Number.isInteger(place.price_level) && place.price_level >= 0 && place.price_level <= 4 ? place.price_level : null,
        michelinGuide: findMichelinGuide(place),
        scheduleInfo,
        distance,
        walkMinutes: distance == null ? null : Math.max(1, Math.round(distance / 80)),
        cuisineLabel: this.getCuisineLabel(place),
        recommendationReasons: this.getReasons(place, distance, scheduleInfo, visitReason || (place.reviewEvidence ? 'AI：取得的評論有餐點好評' : interestReason)),
        _arrivalRank: !preferences.schedule || scheduleInfo?.status === 'safe' || scheduleInfo?.status === 'tight' ? 0 : scheduleInfo?.status === 'late' ? 2 : 1,
        _rankScore: score
      };
    }).sort((a, b) => a._arrivalRank - b._arrivalRank || b._rankScore - a._rankScore || a.distance - b.distance || a.place_id.localeCompare(b.place_id));

    const nearestId = [...ranked].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))[0]?.place_id;
    const highestRatedId = [...ranked].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0]?.place_id;
    return ranked.slice(0, preferences.ignoreRating ? 60 : 20).map((place, index) => {
      let matchLabel = index === 0 && place.scheduleInfo?.status === 'safe' ? '準時首選' : index === 0 ? '最符合' : '值得試試';
      if (index > 0 && place.place_id === nearestId) matchLabel = '最近';
      else if (!preferences.ignoreRating && index > 0 && place.place_id === highestRatedId) matchLabel = '高評價';
      const { _rankScore, _distance, _arrivalRank, ...publicPlace } = place;
      return { ...publicPlace, matchLabel };
    });
  }

  async discoverFromReviews(places, location, preferences, onProgress, isCurrent) {
    const nearby = this.getRecommendations(places, location, { ...preferences, priceRange: 'all', openNow: false, ignoreRating: true });
    onProgress('details', 0, nearby.length);
    const refreshed = await googleMapsService.refreshSavedPlaces(nearby, isCurrent);
    if (!isCurrent()) throw new Error('搜尋條件已變更，請重新分析。');
    if (nearby.length && refreshed.failed === nearby.length) throw new Error('店家評論暫時無法取得，請再試一次。');
    const eligible = this.getRecommendations(refreshed.places, location, { ...preferences, ignoreRating: true });
    const positive = [];
    let failed = refreshed.failed;
    let analyzed = 0;
    let mixed = 0;
    let insufficient = 0;
    for (let start = 0; start < eligible.length; start += 3) {
      if (!isCurrent()) throw new Error('搜尋條件已變更，請重新分析。');
      const page = eligible.slice(start, start + 3);
      const batch = page.filter(place => (place.reviews || []).filter(review => typeof review.text === 'string' && review.text.trim()).length >= 2);
      insufficient += page.length - batch.length;
      const accept = (assessments, candidates) => {
        analyzed += assessments.length;
        mixed += assessments.filter(item => item.verdict === 'mixed').length;
        insufficient += assessments.filter(item => item.verdict === 'insufficient').length;
        for (const item of assessments.filter(item => item.verdict === 'positive')) {
          const place = candidates.find(place => place.place_id === item.placeId);
          positive.push({ ...place, reviewEvidence: item.positiveReviewIndexes.map(index => place.reviews[index]) });
        }
      };
      if (batch.length) {
        try { accept(await aiService.assessReviews(batch), batch); }
        catch (error) {
          // Retry malformed batches individually; outages and rate limits should not multiply requests.
          if (batch.length > 1 && /^Invalid/.test(error.message)) {
            for (const place of batch) {
              if (!isCurrent()) throw new Error('搜尋條件已變更，請重新分析。');
              try { accept(await aiService.assessReviews([place]), [place]); } catch { failed++; }
            }
          } else failed += batch.length;
        }
      }
      onProgress('analysis', Math.min(start + 3, eligible.length), eligible.length);
    }
    if (!isCurrent()) throw new Error('搜尋條件已變更，請重新分析。');
    if (eligible.length && !analyzed && failed > refreshed.failed) throw new Error('AI 評論分析暫時失敗，請再試一次。');
    return { places: positive, searched: places.length, eligible: eligible.length, analyzed, failed, mixed, insufficient };
  }

  getReasons(place, distance, scheduleInfo, interestReason) {
    const reasons = [];
    if (scheduleInfo?.status === 'safe') reasons.push(`可準時抵達，餘裕 ${scheduleInfo.slackMinutes} 分鐘`);
    if (interestReason) reasons.push(interestReason);
    if (distance != null) reasons.push(`步行約 ${Math.max(1, Math.round(distance / 80))} 分鐘`);
    if (Number.isFinite(place.rating) && place.rating >= 1 && place.rating <= 5 && Number.isFinite(place.user_ratings_total) && place.user_ratings_total > 0) {
      reasons.push(`${place.rating.toFixed(1)} 分・${place.user_ratings_total.toLocaleString()} 則評論`);
    }
    if (place.opening_hours?.open_now) reasons.push('目前營業中');
    return reasons.slice(0, 2);
  }

  getCuisineLabel(place) {
    const types = Array.isArray(place.types) ? place.types : [];
    const known = types.find(type => type !== 'restaurant' && TYPE_LABELS[type]) || types.find(type => TYPE_LABELS[type]);
    return TYPE_LABELS[known] || '餐飲';
  }

  getContextualRecommendations(places) {
    return places;
  }
}

export default new RecommendationService();
